import React, { useEffect } from 'react'
import { If, Loader } from '@paytrace/pt-skel/Component'
import { useMountUnmount } from '@paytrace/pt-skel/Hooks'
import * as url from '../Helper/Url'
import { TransactionHistorySearch } from '@paytrace/pt-sdk'
import ViewTransactionResult, {
  hasNoSearchResults,
  hasSearchError,
  isSearchingState,
  isSuccessSearchState,
  NoResultsFound,
} from '../Component/ViewTransactionResult'
import {
  clearTranxCache,
  loadTranxCache,
  useTransactionSearch,
} from '../hooks/useTransactionSearch'
import { SearchStatus } from '../types'
import './ViewTransactions.css'

const self = Symbol('ViewTransactionResultsOnly')

export function ViewTransactionsResultsOnly () {
  const {
    doSearch,
    searchResponse,
    setSearchResponse,
    status,
    setStatus,
    activeSearchCriteria,
    setActiveSearchCriteria,
  } = useTransactionSearch(self)

  useMountUnmount(() => {
    const loadTransactionData = async () => {
      const queryString = new URLSearchParams(window.location.search).get('q')

      if (!queryString) return

      let searchCriteria = null
      let loadedFromCache = false

      try {
        const query = url.decodeUrlQueryParams<TransactionHistorySearch>(queryString)
        searchCriteria = query.parameters.initialStates?.searchCriteria

        const cacheKey = query.parameters.initialStates?.cacheKey
        const cachedData = await loadTranxCache(cacheKey)

        if (cachedData && searchCriteria) {
          setStatus(SearchStatus.Success)
          setSearchResponse(cachedData)
          setActiveSearchCriteria(searchCriteria)
          loadedFromCache = true
        }
      } catch (err) {
        setStatus(SearchStatus.Error)
      } finally {
        clearTranxCache()
      }

      // when page is refreshed it will have to make another api call
      if (!loadedFromCache && searchCriteria !== null && searchCriteria !== undefined) {
        doSearch(searchCriteria)
      }
    }
    loadTransactionData()

    return () => {}
  })

  useEffect(() => {
    const print = new URLSearchParams(window.location.search).get('print')

    if (print === 'y' && status === SearchStatus.Success) {
      // Short delay ensures the DOM and any styling have painted
      const timer = setTimeout(() => {
        window.print()
      }, 300)

      return () => clearTimeout(timer)
    }

    return undefined
  }, [status])

  return (
    <>
      <If is={isSearchingState(status, searchResponse)}>
        <Loader />
      </If>

      <If is={hasNoSearchResults(status, searchResponse)}>
        <NoResultsFound />
      </If>

      <If is={hasSearchError(status, searchResponse)}>
        <div className="no-results-found">An error occurred during search.</div>
      </If>

      <If
        is={
          isSuccessSearchState(status, searchResponse) &&
          !hasNoSearchResults(status, searchResponse)
        }
      >
        <ViewTransactionResult
          summary={searchResponse?.summary}
          cardSummaries={searchResponse?.cardSummaries}
          transactions={searchResponse?.transactions}
          searchStatus={status}
          searchCriteria={activeSearchCriteria}
          // show the results with or without buttons and overflow
          showAll={true}
        />
      </If>
    </>
  )
}

export default ViewTransactionsResultsOnly
